import { useEffect, useRef } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { fetchAndStoreMessages } from '../../lib/twilio';

interface MessagePollerProps {
  businessId: string;
  enabled: boolean;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  interval?: number; // Polling interval in milliseconds
}

export function MessagePoller({
  businessId,
  enabled,
  accountSid,
  authToken,
  phoneNumber,
  interval = 30000, // Default to 30 seconds
}: MessagePollerProps) {
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // If not enabled or missing credentials, don't start polling
    if (!enabled || !accountSid || !authToken || !phoneNumber) {
      return;
    }

    // Function to fetch messages
    const pollMessages = () => {
      fetchAndStoreMessages({
        businessId,
        accountSid,
        authToken,
        phoneNumber,
      });
    };

    // Fetch immediately
    pollMessages();

    // Set up polling interval
    timerRef.current = setInterval(pollMessages, interval);

    // Cleanup on unmount or when props change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [businessId, enabled, accountSid, authToken, phoneNumber, interval]);

  // This is a background component, so it doesn't render anything
  return null;
} 